/**
 * The first screen anybody sees: we are reading their site.
 *
 * It said one fact three times. "4 of 10" in bold, "40%" in a pill beside it,
 * and a bar underneath showing the same fraction in a gradient that ran from
 * the accent through chartreuse into emerald. The count is stated once now,
 * and the bar is the picture of it.
 *
 * Two of the things it said were not true. The bar was floored at 8%, so it
 * showed progress before any had happened, on the one screen whose whole job
 * is to report progress honestly. And the first step was marked `active: true`
 * unconditionally and drew a tick, which means finished, from the moment the
 * screen appeared. The steps report what is actually happening.
 *
 * It also opened with a monospace uppercase "PREPARING REPORT" above a serif
 * headline, and carried two more of those, one over a box explaining the ten
 * page limit. The limit is a sentence in the lead now, which is where somebody
 * reads it without opening anything.
 */
import { createPortal } from 'react-dom'
import { Check } from 'lucide-react'
import { DsIsland } from '@/design-system/island'
import { HubSpotFound } from '@/components/onboarding/HubSpotFound'
import { PublicScanFrame } from '@/components/onboarding/PublicScanFrame'
import { Spinner } from '@/design-system/feedback'
import '@/design-system/onboarding.css'

export interface PublicScanPrepProgress {
  status?: string
  currentStep?: string
  message?: string
  pagesAnalyzed?: number
  totalPages?: number | null
  phase?: 'discovery' | 'crawling' | 'analyzing' | 'scoring'
  /** What the site runs on, once the scan has looked. */
  platform?: 'hubspot' | 'other' | null
  hubspotOnly?: boolean
}

type StepState = 'done' | 'doing' | 'waiting'

/**
 * Who is waiting. `public` is the scan anybody can start from the site, and
 * is what this screen has always been. `account` is the read a signed-in
 * person starts under sign-up first: no interview follows it, and whether
 * the site is on HubSpot is not known when the screen first appears, so it
 * says only what is true of every read, from the first paint to the last.
 */
export type PublicScanPrepVariant = 'public' | 'account'

export function PublicScanPrepScreen({
  progress,
  variant = 'public',
}: {
  progress?: PublicScanPrepProgress | null
  variant?: PublicScanPrepVariant
}) {
  const totalPages = progress?.totalPages || 10
  const pagesAnalyzed = Math.min(progress?.pagesAnalyzed || 0, totalPages)
  /* No floor. A bar that starts at 8% is reporting work that has not happened,
     on the screen whose only job is to report the work honestly. */
  const percent = totalPages > 0 ? Math.min(100, (pagesAnalyzed / totalPages) * 100) : 0
  const currentMessage = progress?.message || progress?.currentStep || 'Getting started'

  const settled = progress?.status === 'completed' || progress?.status === 'partial'
  const reading: StepState = pagesAnalyzed > 0 || settled ? 'done' : 'doing'
  const working: StepState = settled ? 'done' : pagesAnalyzed > 0 ? 'doing' : 'waiting'
  const asking: StepState = settled ? 'doing' : 'waiting'

  const onHubSpot = progress?.platform === 'hubspot'
  /* On a HubSpot site with the HubSpot-only flow on, nobody is asked any
     questions: the third thing that happens is every page being read again
     and compared with what AI is told. The list says what will happen, so
     it changes with what will happen. */
  const hubspotFlow = Boolean(progress?.hubspotOnly && onHubSpot)

  /* The account's read. It is never followed by the interview, so the
     interview's list would be untrue of it; and the HubSpot list promises a
     fix, which a site that turns out not to be on HubSpot never gets. What
     is left is what happens to every site: its pages are read, and what
     each one tells AI is noted. */
  const account = variant === 'account'

  /* Different walks, so different lists. The interview flow works out the
     business and drafts questions; the HubSpot flow checks every page and
     writes the fix for one. The list says what will happen. */
  const steps: { what: string; why: string; state: StepState }[] = account
    ? [
        {
          what: 'Reading your pages',
          why: 'The ones most likely to shape what AI says about you.',
          state: reading,
        },
        {
          what: 'Noting what AI is told',
          why: 'Every page we read, next to what it says.',
          state: working,
        },
      ]
    : hubspotFlow
    ? [
        {
          what: 'Reading your pages',
          why: 'The ones most likely to shape what AI says about you.',
          state: reading,
        },
        {
          what: 'Checking what AI is told',
          why: 'Every page we read, next to what it says.',
          state: working,
        },
        {
          what: 'Writing the fix',
          why: 'For the page where it does the most. You see it next.',
          state: asking,
        },
      ]
    : [
        {
          what: 'Reading your pages',
          why: 'The ones most likely to shape what an assistant says about you.',
          state: reading,
        },
        {
          what: 'Working out what you do',
          why: 'Your category, who you sell to, what you offer, who you are up against.',
          state: working,
        },
        {
          what: 'Drafting the questions',
          why: 'The ones your buyers would ask, which we put to the assistants.',
          state: asking,
        },
      ]

  return (
    <DsIsland panel={false} ground={false} className="ds-prep">
      <h1 className="ds-h2">Reading your site</h1>
      {onHubSpot ? (
        /* The one line on this screen that is about them rather than about
           the work. A sentence in bold read as one more line of the lead and
           the person it was for scrolled past it. */
        <HubSpotFound testId="prep-platform" />
      ) : null}
      <p className="ds-prep__lead">
        {account
          ? 'Reading your site the way AI does. About a minute.'
          : hubspotFlow
          ? 'We read up to ten of your most important pages, pick the one AI most needs to understand, and write the fix for it. A first look, not every page you have.'
          : 'We look at up to ten of your most important pages, then show you what we worked out about your business so you can correct it. This is a first read, not every page you have.'}
      </p>

      <div className="ds-prep__count">
        <span className="ds-prep__n">{pagesAnalyzed}</span>
        <span className="ds-prep__of">of {totalPages} pages read</span>
      </div>
      <div
        className="ds-prep__bar"
        role="progressbar"
        aria-valuenow={pagesAnalyzed}
        aria-valuemin={0}
        aria-valuemax={totalPages}
        aria-label="Pages read"
      >
        <div className="ds-prep__fill" style={{ width: `${percent}%` }} />
      </div>
      <p className="ds-prep__now">{currentMessage}</p>

      <ul className="ds-ostep__list">
        {steps.map((step) => (
          <li key={step.what} className="ds-ostep" data-state={step.state}>
            <span className="ds-ostep__tick" aria-hidden="true">
              {step.state === 'done' ? <Check /> : step.state === 'doing' ? <Spinner quiet /> : null}
            </span>
            <div>
              <div className="ds-ostep__what">{step.what}</div>
              <p className="ds-ostep__why">{step.why}</p>
            </div>
          </li>
        ))}
      </ul>
    </DsIsland>
  )
}

/**
 * The whole window, while a scan is starting from a marketing page.
 *
 * Rendered through a portal onto `document.body`, and that is not optional.
 * It is `position: fixed` with a high z-index, and neither of those escapes a
 * stacking context. It used to render where it was used, inside the hero's
 * words column, which is `position: relative; z-index: 2` inside an isolated
 * row. Its z-index was then ranked only against its siblings in that column,
 * and the hero card (z-index 3 in the same row), the sticky header, and the
 * demonstration further down the page all painted over it. For the second
 * the scan took to start, the homepage sat on top of "Reading your site" in
 * pieces. On the body there is no ancestor to rank it under.
 *
 * It draws the same frame as the scan page, on purpose: see PublicScanFrame.
 */
export function PublicScanPrepOverlay() {
  return createPortal(
    <PublicScanFrame className="ds-prep-over">
      {/* DsIsland takes no arbitrary props, so the live region is the element
          it wraps rather than the scope itself. */}
      <div role="status" aria-live="polite">
        <PublicScanPrepScreen />
      </div>
    </PublicScanFrame>,
    document.body,
  )
}
